import React from 'react'
import styles from './DeliveryHistoryPanel.module.scss'
import { Popconfirm, Spin } from 'antd'
import {
  DeleteOutlined,
  FileExcelOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { useGetDeliveryHistoryQuery } from '../../api/invoice-delivery-api-client'
import { InvoiceDeliveryItemDto } from '../../api/dto/invoice-delivery.dto'
import {
  ActionButtonsRow,
  ArchiveButton,
  FileNameCell,
  IconButton,
  StatusBadge,
  UnmatchedTag,
  VersionBadge,
} from './BulkInvoiceUpload.styles'

import { UploadedMeta } from './UploadedMeta'
import {
  resolveStatusBadgeKey,
  resolveStatusLabel,
  triggerFileDownload,
} from './utils'
import { EmailDeliveryResults } from './EmailDeliveryResults'
import { useCanDownloadFromInvoiceManagement } from './useCanDownloadFromInvoiceManagement'

interface DeliveryHistoryPanelProps {
  deliveryId: string
  onSetArchived: (id: string, isArchived: boolean) => void
  onDelete: (id: string) => void
  isMutating?: boolean
}

const DeliveryHistoryPanel: React.FC<DeliveryHistoryPanelProps> = ({
  deliveryId,
  onSetArchived,
  onDelete,
  isMutating = false,
}) => {
  const { data, isLoading, isError, error } =
    useGetDeliveryHistoryQuery(deliveryId)
  const canDownloadInvoices = useCanDownloadFromInvoiceManagement()

  if (isLoading) {
    return (
      <div className={styles.historyMessageLoading}>
        <Spin size="small" /> Loading version history…
      </div>
    )
  }

  if (isError) {
    return (
      <div className={styles.historyMessageError}>
        Failed to load history: {error?.message ?? 'Unknown error'}
      </div>
    )
  }

  const versions = (data?.versions ?? []).filter(
    (v: InvoiceDeliveryItemDto) => !v.isLatest
  )
  if (versions.length === 0) {
    return null
  }

  return (
    <div className={styles.historyContainer}>
      {versions.map((record) => (
        <div
          key={record.id}
          className={`${styles.historyItem} ${record.isArchived ? styles.historyItemArchived : ''}`}
        >
          <div className={styles.historyItemHeader}>
            <VersionBadge $isHistorical>v{record.version}</VersionBadge>
            <div className={styles.historyFileBlock}>
              {record.downloadUrl && canDownloadInvoices ? (
                <FileNameCell
                  $clickable
                  onClick={(e) =>
                    triggerFileDownload(
                      e,
                      record.downloadUrl ?? '',
                      record.fileName
                    )
                  }
                  className={styles.fileNameCellFlex}
                >
                  <FileExcelOutlined className={styles.fileIcon} />
                  <span title={record.fileName}>{record.fileName}</span>
                </FileNameCell>
              ) : (
                <FileNameCell className={styles.fileNameCellFlex}>
                  <FileExcelOutlined className={styles.fileIcon} />
                  <span title={record.fileName}>{record.fileName}</span>
                </FileNameCell>
              )}
              <UploadedMeta
                createdAt={record.createdAt}
                uploaderName={record.createdByName}
                uploaderEmail={record.createdByEmail}
              />
            </div>
          </div>

          <div className={styles.historyItemMeta}>
            <StatusBadge $status={resolveStatusBadgeKey(record)}>
              {resolveStatusLabel(record)}
            </StatusBadge>
            {!record.clientId && (
              <UnmatchedTag>
                <WarningOutlined /> Unmatched
              </UnmatchedTag>
            )}
            <ActionButtonsRow className={styles.actionsRight}>
              <ArchiveButton
                $archived={record.isArchived}
                onClick={(e) => {
                  e.stopPropagation()
                  onSetArchived(record.id, !record.isArchived)
                }}
                disabled={isMutating}
              >
                {record.isArchived ? 'Unarchive' : 'Archive'}
              </ArchiveButton>
              <Popconfirm
                title="Delete this version"
                description="This will permanently remove this version. Are you sure?"
                onConfirm={() => onDelete(record.id)}
                okText="Delete"
                okButtonProps={{ danger: true }}
                cancelText="Cancel"
              >
                <IconButton $color="#dc2626" disabled={isMutating}>
                  <DeleteOutlined />
                </IconButton>
              </Popconfirm>
            </ActionButtonsRow>
          </div>

          <EmailDeliveryResults deliveryId={record.id} status={record.status} />
        </div>
      ))}
    </div>
  )
}

export { DeliveryHistoryPanel }
